import React, { useEffect, useState, useCallback } from 'react';
import {
    Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
    Paper, Typography, Box, Modal, Button, IconButton, Chip, TextField,
    Select, MenuItem, Tooltip, Pagination, CircularProgress
} from '@mui/material';
import { Icon } from '@iconify/react';
import { getData } from '../../../webService/webservice.js';
import { toast } from 'react-toastify';
import '../../../styles/Auth/package-management.css';

const PosUsageHistoryPage = () => {
    const [stores, setStores] = useState([]);
    const [loading, setLoading] = useState(false);
    const role = localStorage.getItem("role");

    // Pagination & Filters
    const [page, setPage] = useState(1);
    const [limit] = useState(10);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);

    const [searchQuery, setSearchQuery] = useState('');
    const [dateFilter, setDateFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('All');

    // Summary Cards Stats
    const [summary, setSummary] = useState({

        total99Stores: 0,
        posUsedStores: 0,
        posNotUsedStores: 0
    });
    // Details Modal State
    const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
    const [selectedStore, setSelectedStore] = useState(null);

    const fetchPosUsageData = useCallback(async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem("Token");
            const params = {
                page,
                limit,
                search: searchQuery,
                dateFilter: dateFilter,
                usageStatus: statusFilter
            };
            const res = await getData('store-subscriptions/pos-usage-history', params, token);
            if (res?.status === 200 || res?.success) {
                const data = res.data || {};
                setStores(data.stores || []);
                setSummary(data.summary || { total99Stores: 0, posUsedStores: 0, posNotUsedStores: 0 });
                setTotalPages(data.totalPages || 1);
                setTotalCount(data.totalCount || 0);
            } else {
                toast.error(res?.message || "Failed to load POS usage history");
            }
        } catch (error) {
            console.error("Error fetching POS usage history:", error);
            toast.error("Error connecting to server");
        } finally {
            setLoading(false);
        }
    }, [page, limit, searchQuery, dateFilter, statusFilter]);

    useEffect(() => {
        fetchPosUsageData();
    }, [fetchPosUsageData]);

    const handleSearchChange = (e) => {
        setSearchQuery(e.target.value);
        setPage(1);
    };

    const handleDateFilterChange = (e) => {
        setDateFilter(e.target.value);
        setPage(1);
    };

    const handleStatusFilterChange = (e) => {
        setStatusFilter(e.target.value);
        setPage(1);
    };

    const handleOpenDetails = (store) => {
        setSelectedStore(store);
        setIsDetailsModalOpen(true);
    };

    const handleCloseDetails = () => {
        setIsDetailsModalOpen(false);
        setSelectedStore(null);
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return 'N/A';
        try {
            const d = new Date(dateStr);
            if (isNaN(d.getTime())) return 'N/A';
            return d.toLocaleDateString('en-IN', {
                day: '2-digit',
                month: 'short',
                year: 'numeric'
            });
        } catch (e) {
            return 'N/A';
        }
    };

    const formatDateTime = (dateStr) => {
        if (!dateStr) return 'N/A';
        try {
            const d = new Date(dateStr);
            if (isNaN(d.getTime())) return 'N/A';
            return d.toLocaleString('en-IN', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                hour12: true
            });
        } catch (e) {
            return 'N/A';
        }
    };

    const getDateFilterLabel = (filterKey) => {
        switch (filterKey) {
            case 'today': return 'Today';
            case 'last7Days': return 'Last 7 Days';
            case 'lastMonth': return 'Last Month';
            case 'last6Months': return 'Last 6 Months';
            case 'lastYear': return 'Last Year';
            case 'all': return 'All Time';
            default: return 'All Time';
        }
    };

    return (
        <div className="product-layout">
            <Box className="package-management-container">
                {/* Header */}
                <Box className="package-header">
                    <Box className="package-header-title">
                        <Box className="package-header-icon">
                            <Icon icon="mdi:history" width="24" height="24" />
                        </Box>
                        <Box>
                            <Typography variant="h5" component="h2" style={{ fontWeight: 700, color: '#0f172a' }}>
                                POS Usage History
                            </Typography>
                            <Typography variant="body2" style={{ color: '#64748b' }}>
                                Track whether Stores are actively generating POS bills
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                {/* Summary Cards (Only 3 summary cards) */}
                <Box className="package-stats-grid">
                    <Box className="package-stat-card">
                        <Box className="stat-icon-wrapper all">
                            <Icon icon="mdi:store" />
                        </Box>
                        <Box>
                            <Typography variant="body2" style={{ color: '#64748b', fontWeight: 500 }}>
                                Total Stores
                            </Typography>
                            <Typography variant="h5" style={{ fontWeight: 700, color: '#0f172a' }}>
                                {summary.total99Stores || 0}
                            </Typography>
                        </Box>
                    </Box>

                    <Box className="package-stat-card">
                        <Box className="stat-icon-wrapper active">
                            <Icon icon="mdi:check-circle" />
                        </Box>
                        <Box>
                            <Typography variant="body2" style={{ color: '#64748b', fontWeight: 500 }}>
                                POS Used Stores
                            </Typography>
                            <Typography variant="h5" style={{ fontWeight: 700, color: '#16a34a' }}>
                                {summary.posUsedStores || 0}
                            </Typography>
                        </Box>
                    </Box>

                    <Box className="package-stat-card">
                        <Box className="stat-icon-wrapper expired">
                            <Icon icon="mdi:close-circle" />
                        </Box>
                        <Box>
                            <Typography variant="body2" style={{ color: '#64748b', fontWeight: 500 }}>
                                POS Not Used Stores
                            </Typography>
                            <Typography variant="h5" style={{ fontWeight: 700, color: '#dc2626' }}>
                                {summary.posNotUsedStores || 0}
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                {/* Controls & Filters Card */}
                <div className="package-controls-card">
                    <div className="search-box-wrapper">
                        <Icon icon="mdi:magnify" width="20" height="20" color="#64748b" />
                        <input
                            type="text"
                            placeholder="Search by store name..."
                            value={searchQuery}
                            onChange={handleSearchChange}
                        />
                        {searchQuery && (
                            <IconButton size="small" onClick={() => { setSearchQuery(''); setPage(1); }}>
                                <Icon icon="mdi:close" width="16" height="16" />
                            </IconButton>
                        )}
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                        {/* Date Filter Dropdown */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#475569', fontSize: '13.5px' }}>
                                Date Range:
                            </Typography>
                            <Select
                                size="small"
                                value={dateFilter}
                                onChange={handleDateFilterChange}
                                sx={{
                                    minWidth: 140,
                                    borderRadius: '8px',
                                    fontSize: '13.5px',
                                    backgroundColor: '#ffffff',
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#cbd5e1'
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#3C0008'
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#3C0008'
                                    }
                                }}
                            >
                                <MenuItem value="all">All Time</MenuItem>
                                <MenuItem value="today">Today</MenuItem>
                                <MenuItem value="last7Days">Last 7 Days</MenuItem>
                                <MenuItem value="lastMonth">Last Month</MenuItem>
                                <MenuItem value="last6Months">Last 6 Months</MenuItem>
                                <MenuItem value="lastYear">Last Year</MenuItem>
                            </Select>
                        </div>

                        {/* Usage Status Dropdown */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#475569', fontSize: '13.5px' }}>
                                Usage Status:
                            </Typography>
                            <Select
                                size="small"
                                value={statusFilter}
                                onChange={handleStatusFilterChange}
                                sx={{
                                    minWidth: 130,
                                    borderRadius: '8px',
                                    fontSize: '13.5px',
                                    backgroundColor: '#ffffff',
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#cbd5e1'
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#3C0008'
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#3C0008'
                                    }
                                }}
                            >
                                <MenuItem value="All">All Status</MenuItem>
                                <MenuItem value="Used">Used</MenuItem>
                                <MenuItem value="Not Used">Not Used</MenuItem>
                            </Select>
                        </div>
                    </div>
                </div>

                {/* Main Table Container */}
                <TableContainer component={Paper} elevation={0} style={{ borderRadius: '14px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                    <Table>
                        <TableHead>
                            <TableRow style={{ backgroundColor: '#3C0008' }}>
                                <TableCell style={{ color: '#ffffff', fontWeight: 700, fontSize: '14px' }}>Store Name</TableCell>
                                <TableCell style={{ color: '#ffffff', fontWeight: 700, fontSize: '14px' }}>Store Join Date</TableCell>
                                <TableCell align="center" style={{ color: '#ffffff', fontWeight: 700, fontSize: '14px' }}>Total Bills</TableCell>
                                <TableCell style={{ color: '#ffffff', fontWeight: 700, fontSize: '14px' }}>Last Bill Date</TableCell>
                                <TableCell style={{ color: '#ffffff', fontWeight: 700, fontSize: '14px' }}>POS Usage Status</TableCell>
                                <TableCell align="center" style={{ color: '#ffffff', fontWeight: 700, fontSize: '14px' }}>Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={6} align="center" style={{ padding: '40px' }}>
                                        <CircularProgress size={32} style={{ color: '#3C0008' }} />
                                        <Typography variant="body2" style={{ marginTop: '12px', color: '#64748b' }}>
                                            Loading POS usage history...
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : stores.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={6} align="center" style={{ padding: '40px' }}>
                                        <Icon icon="mdi:store-off-outline" width="48" height="48" style={{ color: '#cbd5e1', marginBottom: '8px' }} />
                                        <Typography variant="subtitle1" style={{ fontWeight: 600, color: '#475569' }}>
                                            No stores found
                                        </Typography>
                                        <Typography variant="body2" style={{ color: '#94a3b8' }}>
                                            Try adjusting your search query or date filter options.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                stores.map((item) => (
                                    <TableRow key={item._id} hover style={{ transition: 'background-color 0.15s ease' }}>
                                        <TableCell style={{ fontWeight: 600, color: '#0f172a' }}>
                                            {item.storeName || 'N/A'}
                                        </TableCell>
                                        <TableCell style={{ color: '#475569', whiteSpace: 'nowrap' }}>
                                            {formatDate(item.subscriptionStartDate || item.storeJoinDate || item.validityStartDate)}
                                        </TableCell>
                                        <TableCell align="center">
                                            <Chip
                                                label={
                                                    dateFilter !== 'all' && item.billsCountInRange !== undefined
                                                        ? `${item.billsCountInRange} bills`
                                                        : `${item.totalBillsCount ?? item.billsCountInRange ?? 0} bills`
                                                }
                                                size="small"
                                                style={{
                                                    backgroundColor: (dateFilter !== 'all' ? item.billsCountInRange : (item.totalBillsCount ?? item.billsCountInRange ?? 0)) > 0 ? '#eff6ff' : '#f8fafc',
                                                    color: (dateFilter !== 'all' ? item.billsCountInRange : (item.totalBillsCount ?? item.billsCountInRange ?? 0)) > 0 ? '#1d4ed8' : '#94a3b8',
                                                    fontWeight: 700,
                                                    border: `1px solid ${(dateFilter !== 'all' ? item.billsCountInRange : (item.totalBillsCount ?? item.billsCountInRange ?? 0)) > 0 ? '#bfdbfe' : '#e2e8f0'}`
                                                }}
                                            />
                                            {dateFilter !== 'all' && item.totalBillsCount !== undefined && (
                                                <Typography variant="caption" style={{ display: 'block', color: '#64748b', fontSize: '11px', marginTop: '2px' }}>
                                                    All-time: {item.totalBillsCount}
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell style={{ color: '#475569', whiteSpace: 'nowrap' }}>
                                            {formatDate(item.lastBillDate)}
                                        </TableCell>
                                        <TableCell>
                                            {item.posUsageStatus === 'Used' ? (
                                                <Chip
                                                    icon={<Icon icon="mdi:check-circle" style={{ color: '#16a34a' }} />}
                                                    label="Used"
                                                    size="small"
                                                    style={{
                                                        backgroundColor: '#f0fdf4',
                                                        color: '#15803d',
                                                        fontWeight: 700,
                                                        border: '1px solid #bbf7d0'
                                                    }}
                                                />
                                            ) : (
                                                <Chip
                                                    icon={<Icon icon="mdi:close-circle" style={{ color: '#dc2626' }} />}
                                                    label="Not Used"
                                                    size="small"
                                                    style={{
                                                        backgroundColor: '#fef2f2',
                                                        color: '#b91c1c',
                                                        fontWeight: 700,
                                                        border: '1px solid #fecaca'
                                                    }}
                                                />
                                            )}
                                        </TableCell>
                                        <TableCell align="center">
                                            <Tooltip title="View POS Usage Details">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleOpenDetails(item)}
                                                    style={{
                                                        backgroundColor: '#eff6ff',
                                                        color: '#2563eb',
                                                        borderRadius: '8px',
                                                        padding: '6px'
                                                    }}
                                                >
                                                    <Icon icon="mdi:eye" width="18" height="18" />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 24px', borderTop: '1px solid #e2e8f0' }}>
                            <Typography variant="body2" style={{ color: '#64748b' }}>
                                Showing page {page} of {totalPages} ({totalCount} total stores)
                            </Typography>
                            <Pagination
                                count={totalPages}
                                page={page}
                                onChange={(e, value) => setPage(value)}
                                color="primary"
                                shape="rounded"
                            />
                        </Box>
                    )}
                </TableContainer>

                {/* View POS Usage Status Details Modal */}
                <Modal open={isDetailsModalOpen} onClose={handleCloseDetails}>
                    <Box style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: '90%',
                        maxWidth: '540px',
                        backgroundColor: '#ffffff',
                        borderRadius: '16px',
                        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                        padding: '28px',
                        outline: 'none'
                    }}>
                        <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                            <Box style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <Box style={{
                                    width: '36px',
                                    height: '36px',
                                    borderRadius: '10px',
                                    backgroundColor: '#eff6ff',
                                    color: '#2563eb',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}>
                                    <Icon icon="mdi:information-outline" width="22" height="22" />
                                </Box>
                                <Typography variant="h6" style={{ fontWeight: 700, color: '#0f172a' }}>
                                    POS Usage Status Details
                                </Typography>
                            </Box>
                            <IconButton size="small" onClick={handleCloseDetails}>
                                <Icon icon="mdi:close" width="20" height="20" />
                            </IconButton>
                        </Box>

                        {selectedStore && (
                            <Box style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                {/* Store Header Info */}
                                <Box style={{ backgroundColor: '#f8fafc', padding: '16px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                                    <Typography variant="subtitle2" style={{ color: '#64748b', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                        Store Name
                                    </Typography>
                                    <Typography variant="h6" style={{ fontWeight: 700, color: '#0f172a', marginTop: '2px' }}>
                                        {selectedStore.storeName || 'N/A'}
                                    </Typography>
                                </Box>

                                {/* 2-Column Info Grid for Join Date and Total Bills */}
                                <Box style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: '12px' }}>
                                    {/* Store Join Date */}
                                    <Box style={{ backgroundColor: '#f8fafc', padding: '14px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                                        <Box style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                                            <Icon icon="mdi:calendar-star" width="18" height="18" style={{ color: '#4f46e5' }} />
                                            <Typography variant="caption" style={{ color: '#64748b', fontWeight: 600, textTransform: 'uppercase', fontSize: '11px', letterSpacing: '0.5px' }}>
                                                Store Join Date
                                            </Typography>
                                        </Box>
                                        <Typography variant="subtitle1" style={{ fontWeight: 700, color: '#0f172a' }}>
                                            {formatDate(selectedStore.subscriptionStartDate || selectedStore.storeJoinDate || selectedStore.validityStartDate)}
                                        </Typography>
                                        <Typography variant="caption" style={{ color: '#94a3b8', fontSize: '11px', display: 'block', marginTop: '2px' }}>
                                            Subscription Start Date
                                        </Typography>
                                    </Box>

                                    {/* Total Bills Generated */}
                                    <Box style={{ backgroundColor: '#f8fafc', padding: '14px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                                        <Box style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                                            <Icon icon="mdi:receipt-text-outline" width="18" height="18" style={{ color: '#0284c7' }} />
                                            <Typography variant="caption" style={{ color: '#64748b', fontWeight: 600, textTransform: 'uppercase', fontSize: '11px', letterSpacing: '0.5px' }}>
                                                Total Bills Generated
                                            </Typography>
                                        </Box>
                                        <Typography variant="subtitle1" style={{ fontWeight: 700, color: '#0f172a' }}>
                                            {selectedStore.totalBillsCount ?? selectedStore.billsCountInRange ?? 0} bills
                                        </Typography>
                                        {dateFilter !== 'all' && (
                                            <Typography variant="caption" style={{ color: '#0284c7', fontSize: '11px', display: 'block', marginTop: '2px', fontWeight: 600 }}>
                                                {getDateFilterLabel(dateFilter)}: {selectedStore.billsCountInRange ?? 0} bills
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>

                                {/* Status and Last Bill Date */}
                                <Box style={{ backgroundColor: selectedStore.posUsageStatus === 'Used' ? '#f0fdf4' : '#fef2f2', padding: '16px', borderRadius: '12px', border: `1px solid ${selectedStore.posUsageStatus === 'Used' ? '#bbf7d0' : '#fecaca'}` }}>
                                    <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                        <Typography variant="body2" style={{ fontWeight: 600, color: selectedStore.posUsageStatus === 'Used' ? '#166534' : '#991b1b' }}>
                                            POS Usage Status ({getDateFilterLabel(dateFilter)})
                                        </Typography>
                                        <Chip
                                            label={selectedStore.posUsageStatus}
                                            size="small"
                                            style={{
                                                backgroundColor: selectedStore.posUsageStatus === 'Used' ? '#16a34a' : '#dc2626',
                                                color: '#ffffff',
                                                fontWeight: 700
                                            }}
                                        />
                                    </Box>
                                    <Typography variant="caption" style={{ color: '#475569', display: 'block' }}>
                                        Last POS Bill Date: <strong>{formatDate(selectedStore.lastBillDate)}</strong>
                                    </Typography>
                                </Box>

                                {/* Close Button */}
                                <Box style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '8px' }}>
                                    <Button
                                        variant="contained"
                                        onClick={handleCloseDetails}
                                        style={{
                                            backgroundColor: '#3C0008',
                                            color: '#ffffff',
                                            borderRadius: '8px',
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            padding: '8px 24px'
                                        }}
                                    >
                                        Close
                                    </Button>
                                </Box>
                            </Box>
                        )}
                    </Box>
                </Modal>
            </Box>
        </div>
    );
};

export default PosUsageHistoryPage;
